import React, { useState, useRef } from "react";
import { toast } from "react-hot-toast";
import "./FileUploader.css";

// List of allowed file types
const ALLOWED_FILE_TYPES = {
  ".c": "text/x-c",
  ".cpp": "text/x-c++",
  ".cs": "text/x-csharp",
  ".css": "text/css",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".go": "text/x-golang",
  ".html": "text/html",
  ".java": "text/x-java",
  ".js": "text/javascript",
  ".json": "application/json",
  ".md": "text/markdown",
  ".pdf": "application/pdf",
  ".php": "text/x-php",
  ".pptx":
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".py": "text/x-python",
  ".rb": "text/x-ruby",
  ".sh": "application/x-sh",
  ".tex": "text/x-tex",
  ".ts": "application/typescript",
  ".txt": "text/plain",
};

// Get file extension from filename
const getFileExtension = (filename) => {
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) {
    return ""; // No extension found
  }
  return filename.slice(lastDotIndex + 1).toLowerCase();
};

// Check if file type is allowed
const isFileTypeAllowed = (file) => {
  // Get the extension without the dot
  const extensionWithoutDot = getFileExtension(file.name);
  // Add the dot for checking against our allowed types
  const extension = extensionWithoutDot ? `.${extensionWithoutDot}` : "";
  const mimeType = file.type;

  // console.log(
  //   `File: ${file.name}, Extension: ${extension}, MIME type: ${mimeType}`
  // );

  // Check if extension is in allowed list
  if (!Object.keys(ALLOWED_FILE_TYPES).includes(extension)) {
    // console.log(
    //   `Extension ${extension} not in allowed list:`,
    //   Object.keys(ALLOWED_FILE_TYPES)
    // );
    return false;
  }

  // Some browsers might not report the correct MIME type for .txt files
  // So we'll be more lenient with text files
  if (extension === ".txt") {
    return true;
  }

  // Some browsers might not report the correct MIME type
  // So we'll be lenient if the extension is allowed
  if (!mimeType || mimeType === "application/octet-stream") {
    return true;
  }

  // Check if MIME type matches expected type for this extension
  const expectedMimeType = ALLOWED_FILE_TYPES[extension];

  // Allow if mime type starts with expected (to handle variations)
  const isAllowed =
    mimeType === expectedMimeType ||
    mimeType.startsWith(expectedMimeType) ||
    expectedMimeType.startsWith(mimeType);

  // console.log(
  //   `Expected MIME: ${expectedMimeType}, Actual MIME: ${mimeType}, Allowed: ${isAllowed}`
  // );

  return isAllowed;
};

const FileUploader = ({ onFileSelected, isUploading }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const validateAndProcessFile = (file) => {
    // Check if file is valid
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size exceeds 10MB limit");
      return;
    }

    // Check file type
    if (!isFileTypeAllowed(file)) {
      // Create a more readable list of allowed extensions
      const allowedExtensions = Object.keys(ALLOWED_FILE_TYPES)
        .sort()
        .join(", ");

      toast.error(
        `File type not allowed. Please upload one of the following types: ${allowedExtensions}`
      );
      return;
    }

    // Set selected file and notify parent WITHOUT uploading
    setSelectedFile(file);
    onFileSelected(file); // This should just set the file, not upload it
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    validateAndProcessFile(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    validateAndProcessFile(file);
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    onFileSelected(null);
    fileInputRef.current.value = "";
  };

  return (
    <div className="project-name-file-uploader">
      <div
        className={`project-name-dropzone ${
          isDragging ? "project-name-dragging" : ""
        } ${selectedFile ? "project-name-has-file" : ""}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={selectedFile ? undefined : handleClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
          accept={Object.keys(ALLOWED_FILE_TYPES).join(",")}
        />

        {selectedFile ? (
          <div className="project-name-selected-file">
            <div className="project-name-file-info">
              <span className="project-name-file-name">
                {selectedFile.name}
              </span>
              <span className="project-name-file-size">
                {selectedFile.size < 1024
                  ? `${selectedFile.size} bytes`
                  : selectedFile.size < 1048576
                  ? `${(selectedFile.size / 1024).toFixed(1)} KB`
                  : `${(selectedFile.size / 1048576).toFixed(1)} MB`}
              </span>
            </div>
            {!isUploading && (
              <button
                type="button"
                className="project-name-remove-file"
                onClick={handleRemoveFile}
              >
                ✕
              </button>
            )}
          </div>
        ) : (
          <div className="project-name-dropzone-content">
            <div className="project-name-upload-icon">
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 16L12 8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 11L12 8 15 11"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 16H16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 20.4V3.6C3 3.26863 3.26863 3 3.6 3H20.4C20.7314 3 21 3.26863 21 3.6V20.4C21 20.7314 20.7314 21 20.4 21H3.6C3.26863 21 3 20.7314 3 20.4Z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </div>
            <p className="project-name-dropzone-text">
              <span className="project-name-dropzone-primary">
                Drag and drop a file here, or click to browse
              </span>
              <span className="project-name-dropzone-secondary">
                Supported file types: .txt, .pdf, .doc, .docx, .md, etc.
              </span>
            </p>
          </div>
        )}
      </div>

      {selectedFile && (
        <button
          type="button"
          className={`project-name-upload-button ${
            isUploading ? "project-name-uploading" : ""
          }`}
          onClick={() => {
            // This is where we trigger the upload
            const uploadEvent = new CustomEvent("upload-file", {
              detail: { file: selectedFile },
            });
            document.dispatchEvent(uploadEvent);

            // Clear the selected file after triggering the upload
            setSelectedFile(null);
            fileInputRef.current.value = "";
          }}
          disabled={isUploading}
        >
          {isUploading ? "Uploading..." : "Upload File"}
        </button>
      )}
    </div>
  );
};

export default FileUploader;
